import {
  Account,
  AccountAddress,
  Ed25519Account,
  InputEntryFunctionData,
  PendingTransactionResponse,
} from "@aptos-labs/ts-sdk";
import { Delegations } from "@decibeltrade/sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useWalletFeePayerWithFallback } from "@/contexts/mutations/wallet-fee-payer";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useUserPreferences } from "@/hooks/use-user-preferences";

import { delegationsQueryKey, DelegationWithMetadata } from "../../queries/delegation";

export const useAddDelegation = () => {
  const queryClient = useQueryClient();
  const { writeDexSdk, subaccountAddress } = useWriteDexSdk();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const [isPendingSignature, setIsPendingSignature] = useState(false);
  const { updateDelegationsMetadata } = useUserPreferences();

  return {
    isPendingSignature,
    addDelegation: useMutation({
      mutationFn: async ({
        account: _account,
        name,
        expiresInDays,
        isMobileQr,
      }: {
        account?: Ed25519Account | AccountAddress;
        name: string;
        isMobileQr: boolean;
        expiresInDays?: number;
      }) => {
        if (!writeDexSdk) throw new Error("writeDexSdk is not available");
        if (!subaccountAddress) throw new Error("Subaccount not found");

        const sessionKey = _account ?? Account.generate();

        const sessionKeyAddress =
          sessionKey instanceof AccountAddress
            ? sessionKey.toString()
            : sessionKey.accountAddress.toString();

        const expirationTimestampSecs = expiresInDays
          ? Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60
          : undefined;

        const transactionData: InputEntryFunctionData = {
          function: `${writeDexSdk.config.deployment.package}::dex_accounts_entry::delegate_trading_to_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddress, sessionKeyAddress, expirationTimestampSecs],
        };

        let pendingTransaction: PendingTransactionResponse;

        setIsPendingSignature(true);

        try {
          pendingTransaction = await signAndSubmitWalletTransaction({
            data: transactionData,
            onSignSuccess: () => setIsPendingSignature(false), // Reset the pending signature state on success
          });
        } finally {
          setIsPendingSignature(false); // Reset the pending signature state on error
        }

        await writeDexSdk.aptos.waitForTransaction({
          transactionHash: pendingTransaction.hash,
        });

        await updateDelegationsMetadata({
          [sessionKeyAddress]: {
            name,
            address: sessionKeyAddress,
            isMobileQr,
          },
        });

        queryClient.setQueryData(
          delegationsQueryKey({ subAddr: subaccountAddress }),
          (prev: Delegations | undefined = []) => [
            ...prev,
            {
              delegated_account: sessionKeyAddress,
              permission_type: "trading",
              expiration_time_s: expirationTimestampSecs,
              metadata: {
                name,
                address: sessionKeyAddress,
                isMobileQr,
              },
            } as DelegationWithMetadata,
          ],
        );

        return sessionKeyAddress;
      },
    }),
  };
};
